'use client';

/**
 * ListActionBar
 *
 * Primary action surface on the list detail page.
 *
 * Layout:  [Share]  [⋯ More ▾]
 *
 * More menu contains:
 *   - Pin / Unpin     (non-owners, signed-in)
 *   - Copy to library (non-owners, signed-in, non-private)
 *   - Delete          (owner only, destructive)
 *
 * Collaborator management is surfaced via the Collab button in the header.
 *
 * The Edit button has been removed — editing is now inline on the detail page.
 */

import { useState } from 'react';
import { Share2, MoreHorizontal, Pin, Copy, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useMutation } from '@tanstack/react-query';
import { copyCollection, togglePin } from '@/lib/api';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';

interface ListActionBarProps {
  collectionId: string;
  listTitle: string;
  /** Whether the current user is the list owner. */
  isOwner: boolean;
  /** Whether a signed-in non-owner can copy the list. */
  canCopy: boolean;
  /** Whether a signed-in non-owner can pin the list. */
  canPin: boolean;
  /** Initial pin state (SSR). */
  initialPinned: boolean;
}

export function ListActionBar({
  collectionId,
  listTitle,
  isOwner,
  canCopy,
  canPin,
  initialPinned,
}: ListActionBarProps) {
  const router = useRouter();
  const [pinned, setPinned] = useState(initialPinned);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingList, setDeletingList] = useState(false);

  // Anything in the More menu needs it to stay open while a confirm dialog
  // is appearing — keep the dropdown uncontrolled so it closes naturally.

  // ── Copy mutation ──────────────────────────────────────────────────────────
  const { mutate: copy, isPending: isCopying } = useMutation({
    mutationFn: () => copyCollection(collectionId),
    onSuccess: (data) => {
      const newId = data.collection._id;
      toast.success('Copied to your library', {
        action: {
          label: 'Open',
          onClick: () => { window.location.href = `/lists/${newId}`; },
        },
      });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to copy'),
  });

  // ── Pin mutation ───────────────────────────────────────────────────────────
  const { mutate: pin, isPending: isPinning } = useMutation({
    mutationFn: () => togglePin(collectionId),
    onMutate: () => setPinned((p) => !p),
    onSuccess: (data) => {
      setPinned(data.pinned);
      toast.success(data.pinned ? 'Pinned to dashboard' : 'Unpinned from dashboard');
    },
    onError: () => { setPinned((p) => !p); toast.error('Failed to update pin'); },
  });

  // ── Share ──────────────────────────────────────────────────────────────────
  async function handleShare() {
    const url = `${window.location.origin}/lists/${collectionId}`;
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try { await navigator.share({ title: listTitle, url }); return; } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  async function handleDelete() {
    setDeletingList(true);
    try {
      const res = await fetch(`/api/collections/${collectionId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to delete list');
      }
      toast.success('List deleted');
      router.push('/lists');
      router.refresh();
    } catch (err: any) {
      toast.error(err.message ?? 'Something went wrong');
      setDeletingList(false);
    }
  }

  // Whether the More menu has any entries to show
  const hasMoreItems = canPin || canCopy || isOwner;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Share — always visible */}
        <Button variant="outline" size="sm" className="gap-1.5" onClick={handleShare}>
          <Share2 className="h-3.5 w-3.5" />
          Share
        </Button>

        {/* ⋯ More dropdown */}
        {hasMoreItems && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <MoreHorizontal className="h-3.5 w-3.5" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">

              {/* Pin / Unpin */}
              {canPin && (
                <DropdownMenuItem
                  onSelect={() => pin()}
                  disabled={isPinning}
                  className="gap-2"
                >
                  <Pin className={cn('h-4 w-4', pinned && 'fill-current')} />
                  {pinned ? 'Unpin' : 'Pin to dashboard'}
                </DropdownMenuItem>
              )}

              {/* Copy to library */}
              {canCopy && (
                <DropdownMenuItem
                  onSelect={() => copy()}
                  disabled={isCopying}
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {isCopying ? 'Copying…' : 'Copy to library'}
                </DropdownMenuItem>
              )}

              {/* Destructive section */}
              {isOwner && (canPin || canCopy) && <DropdownMenuSeparator />}
              {isOwner && (
                <DropdownMenuItem
                  onSelect={() => setShowDeleteConfirm(true)}
                  className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete list
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{listTitle}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the list and all its items. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deletingList}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingList ? 'Deleting…' : 'Delete list'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
